import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { PURPOSE_LABELS, DELIVERY_LABELS, StatusBadge, canOrder, fullAddress } from "@/lib/cases";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { CopyLink } from "@/components/app/CopyLink";
import { Tick } from "@/components/app/Tick";
import { CaseWork } from "@/components/app/CaseWork";

const Card = ({ title, children, testid }) => (
  <section className="rounded-3xl border border-gray-200 bg-white p-6" data-testid={testid}>
    <h2 className="font-mono-label text-[10px] font-semibold text-oath-muted">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 border-b border-gray-100 py-2 text-sm last:border-0">
    <span className="text-oath-secondary">{k}</span><span className="text-right text-oath-ink" data-testid={testid}>{v || "—"}</span>
  </div>
);

const ResendPanel = ({ c, smsEnabled, onDone }) => {
  const [via, setVia] = useState({ email: !!c.subject.email, sms: false });
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/cases/${c.id}/resend`, { deliver_via: Object.keys(via).filter((k) => via[k]) });
      setLink(data);
      toast.success("New consent link issued");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid gap-2" data-testid="resend-panel">
      <div className="grid gap-2 sm:grid-cols-2">
        <Tick checked={via.email} disabled={!c.subject.email} onToggle={() => setVia({ ...via, email: !via.email })} label="Email" testid="resend-email" hint={c.subject.email || "No email on file"} />
        <Tick checked={via.sms} disabled={!smsEnabled} onToggle={() => setVia({ ...via, sms: !via.sms })} label="Text message" testid="resend-sms" hint={smsEnabled ? c.subject.phone : "Not set up yet"} />
      </div>
      <button onClick={run} disabled={loading} data-testid="resend-submit-button"
        className="inline-flex w-fit items-center gap-2 rounded-full bg-oath-ink px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Issue new link
      </button>
      {link && (
        <div className="mt-2" data-testid="resend-result">
          {link.deliveries.map((d) => <div key={d.channel} className="text-xs"><b className="uppercase">{d.channel}</b>: {DELIVERY_LABELS[d.status]}{d.error ? ` — ${d.error}` : ""}</div>)}
          <div className="mt-2"><CopyLink path={link.consent_path} testid="resend-consent-link" /></div>
        </div>
      )}
    </div>
  );
};

const Actions = ({ c, onChanged }) => {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => {
    api.get("/cases/meta").then(({ data }) => setSmsEnabled(data.sms_enabled)).catch(() => {});
  }, []);
  const cancel = async () => {
    try {
      await api.post(`/cases/${c.id}/cancel`);
      toast.success("Case cancelled");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const canResend = ["awaiting_consent", "consent_expired", "consent_locked"].includes(c.status);
  const canCancel = canResend || c.status === "consent_declined";
  if (!canResend && !canCancel) return null;
  return (
    <Card title="ACTIONS" testid="case-actions">
      {canResend && <ResendPanel c={c} smsEnabled={smsEnabled} onDone={onChanged} />}
      {canCancel && (
        <div className="mt-5 border-t border-gray-100 pt-4">
          {confirmCancel ? (
            <span className="flex gap-2">
              <button onClick={cancel} className="rounded-full bg-red-600 px-4 py-2 text-xs text-white" data-testid="case-cancel-confirm-button">Yes, cancel case</button>
              <button onClick={() => setConfirmCancel(false)} className="rounded-full px-3 py-2 text-xs text-oath-muted">Keep</button>
            </span>
          ) : (
            <button onClick={() => setConfirmCancel(true)} className="text-xs text-red-600 underline" data-testid="case-cancel-button">Cancel this case</button>
          )}
        </div>
      )}
    </Card>
  );
};

export default function CaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setC(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setC(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (c === null) return <AppShell title="Case"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></AppShell>;
  if (!c) return <AppShell title="Case not found"><Link to="/console/cases" className="text-oath-accent underline">Back to cases</Link></AppShell>;

  const cs = c.consent || {};
  const back = <Link to="/console/cases" className="inline-flex items-center gap-1 text-sm text-oath-secondary hover:text-oath-ink" data-testid="case-back-link"><ArrowLeft className="h-4 w-4" /> Cases</Link>;
  return (
    <AppShell eyebrow={`— CASE ${c.case_number}`} title={`${c.subject.first_name} ${c.subject.last_name}`} actions={back}>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <StatusBadge status={c.status} testid="case-detail-status" />
        {c.priority === "rush" && <span className="rounded-full bg-red-50 px-2.5 py-0.5 font-mono-label text-[9px] text-red-600">RUSH</span>}
        <span className="text-xs text-oath-muted">Opened {fmtDate(c.created_at)} by {c.created_by_name}</span>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="grid gap-5">
          <Card title="ASSERTION UNDER TEST" testid="case-assertion">
            <p className="font-display text-lg text-oath-ink" data-testid="case-address">{fullAddress(c.address)}</p>
            <p className="mt-1 text-sm text-oath-secondary">is the current residence of {c.subject.first_name} {c.subject.last_name}
              {c.household.length ? `, living with ${c.household.length} other${c.household.length > 1 ? "s" : ""}:` : ", with no other household members declared."}</p>
            {c.household.length > 0 && (
              <ul className="mt-3 grid gap-1 text-sm" data-testid="case-household">
                {c.household.map((h, i) => <li key={i} className="text-oath-ink">{h.name} <span className="text-oath-secondary">· {h.relationship}{h.age != null ? ` · ${h.age}` : ""}</span></li>)}
              </ul>
            )}
          </Card>
          <Card title="SUBJECT & REQUEST" testid="case-subject">
            <Row k="Date of birth" v={c.subject.dob} />
            <Row k="Mobile" v={c.subject.phone} />
            <Row k="Email" v={c.subject.email} />
            <Row k="Permissible purpose" v={PURPOSE_LABELS[c.permissible_purpose]} />
            <Row k="Your reference" v={c.reference} />
            <Row k="Notes" v={c.notes} />
          </Card>
          <Card title="TIMELINE" testid="case-timeline">
            <ol className="grid gap-3">
              {[...(c.timeline || [])].reverse().map((t, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-oath-accent" />
                  <div><div className="text-oath-ink">{t.detail}</div><div className="font-mono text-[11px] text-oath-muted">{fmtDate(t.at)}</div></div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
        <div className="grid content-start gap-5">
          <Card title="CONSENT" testid="case-consent">
            <Row k="Link expires" v={fmtDate(cs.expires_at)} />
            <Row k="Opened by subject" v={fmtDate(cs.viewed_at)} />
            <Row k="Identity confirmed" v={fmtDate(cs.identified_at)} />
            <Row k="Signed" v={fmtDate(cs.signed_at)} testid="case-consent-signed" />
            <Row k="Signature" v={cs.signature_name} testid="case-consent-signature" />
            <Row k="Declined" v={fmtDate(cs.declined_at)} />
            {cs.decline_reason && <Row k="Reason given" v={cs.decline_reason} />}
            {cs.ip && <Row k="IP address" v={cs.ip} />}
            {cs.text && <p className="mt-3 rounded-xl bg-oath-bg/70 p-3 text-xs leading-relaxed text-oath-secondary" data-testid="case-consent-text">{cs.text}</p>}
          </Card>
          <Card title="DELIVERY LOG" testid="case-deliveries">
            {(cs.deliveries || []).length === 0 && <p className="text-sm text-oath-muted">Link shared manually only.</p>}
            {(cs.deliveries || []).map((d, i) => (
              <Row key={i} k={`${d.channel.toUpperCase()} · ${fmtDate(d.at)}`} v={`${DELIVERY_LABELS[d.status] || d.status}${d.error ? ` — ${d.error}` : ""}`} />
            ))}
          </Card>
          {canOrder(user) && <Actions c={c} onChanged={load} />}
          <CaseWork c={c} canOrder={canOrder(user)} onChanged={load} />
        </div>
      </div>
    </AppShell>
  );
}
